import Keycloak from 'keycloak-js';

import { logRumAction } from 'lib/DDRum';
import UsageService from 'lib/UsageWrapper';
import { broadcastLogout } from 'lib/authBroadcast';
import { clearBrowserStorage } from 'lib/browserStorage';

import Env from './Env';
import { LOGIN_METADATA } from './constants';

const TOKEN_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

const keycloakConfig = {
  realm: 'Amplify',
  url: Env.KEYCLOAK_URL,
  clientId: 'aqua-app',
};

const keycloakInitConfig = {
  checkLoginIframe: false,
  onLoad: 'login-required',
};

class AuthService {
  constructor() {
    this.keycloakClient = new Keycloak(keycloakConfig);
    this.tokenCheckInterval = null;
    this.isInitialLogin = true;

    // Update stored refresh token expiration on successful token refresh
    // This is a callback listener for keycloakClient.updateToken
    this.keycloakClient.onAuthRefreshSuccess = () => {
      this.storeLoginMetadata();
    };
  }

  init() {
    return new Promise((resolve, reject) => {
      this.keycloakClient
        .init(keycloakInitConfig)
        .then(resolve)
        .catch(reject);
    }).then(() => {
      window.kc = this.keycloakClient;

      // If already logged in to Boost and stored refresh token is valid,
      // update stored refresh token expiration in localStorage
      if (this.hasValidLoginMetadata()) {
        this.isInitialLogin = false;
        this.storeLoginMetadata();
      }

      // Call updateToken every 5 minutes to check if refresh is needed
      this.startTokenCheck();
    }).catch(() => this.logout());
  }

  // Initializes interval to check token validity every 5 minutes
  startTokenCheck() {
    this.tokenCheckInterval = setInterval(() => {
      // If token is 0 seconds from (or past) expiration, attempt refresh
      this.keycloakClient.updateToken(0).catch(() => this.logout());
    }, TOKEN_CHECK_INTERVAL);
  }

  // Clear interval for token check
  clearTokenCheck() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
  }

  // Sets the keycloak session_state and refresh token expiration in localStorage
  // Used to initially set on login or update on token refresh
  storeLoginMetadata() {
    try {
      // This field ensures we store the correct student's kc session and their current login session
      // Edge case: student logs out outside of Boost and same (or different) student logs in, then navigates to Boost
      const sessionState = this.keycloakClient.tokenParsed.session_state;
      // Refresh token expiration is in seconds - convert to milliseconds
      const refreshTokenExp = this.keycloakClient.refreshTokenParsed.exp * 1000;

      const loginMetadata = {
        sessionState,
        refreshTokenExp,
      };
      localStorage.setItem(LOGIN_METADATA, JSON.stringify(loginMetadata));
    } catch (error) {
      console.warn('Error storing login metadata', error);
    }
  }

  // Compares current keycloak session state vs stored session state
  // and checks if the stored refresh token expiration is valid
  hasValidLoginMetadata() {
    try {
      const storedLoginMetadata = localStorage.getItem(LOGIN_METADATA);
      if (storedLoginMetadata) {
        const loginMetadata = JSON.parse(storedLoginMetadata);
        // session_state persists for entire login but refresh token expiration updates in different scenarios
        return loginMetadata.sessionState === this.keycloakClient.tokenParsed.session_state
          && loginMetadata.refreshTokenExp >= Date.now();
      }
      return false;
    } catch (error) {
      console.warn('Error checking stored login metadata', error);
      return false;
    }
  }

  getKC() {
    return this.keycloakClient;
  }

  getCurrentUser() {
    return this.keycloakClient.loadUserInfo();
  }

  getAccessToken() {
    return this.keycloakClient.token;
  }

  getUserBk() {
    return this.keycloakClient.subject;
  }

  getRoles() {
    return this.keycloakClient.realmAccess?.roles || [];
  }

  getSessionId() {
    return this.keycloakClient.sessionId;
  }

  /**
   * Updates the access token if it expires within minValidity seconds.
   * @param {number} minValidity If not specified, 5 is used.
   * @return {Promise<boolean>} Promise that resolves to true if the token was successfully updated, false otherwise.
   */
  async updateToken(minValidity = 5) {
    return this.keycloakClient.updateToken(minValidity);
  }

  /**
   * Clears this tab's auth/usage state and triggers Keycloak logout.
   * Does NOT broadcast to other tabs - this is the handler the cross-tab
   * logout listener invokes, so re-broadcasting would bounce the event
   * back to the originating tab. Callers that need to notify other tabs
   * (logout, logoutTo) call broadcastLogout() before delegating here.
   * @param {string} [redirectUri] Optional post-logout URL. If omitted,
   * Keycloak falls back to its configured default redirect.
   */
  localLogout(redirectUri) {
    this.clearTokenCheck();
    clearBrowserStorage();
    UsageService.stopUsage();
    if (redirectUri) {
      this.keycloakClient.logout({ redirectUri });
    } else {
      // We want the redirect_uri to take the user home
      this.keycloakClient.logout();
    }
  }

  logout() {
    logRumAction('logout');
    broadcastLogout();
    this.localLogout();
  }

  logoutTo(url) {
    logRumAction('logoutTo');
    broadcastLogout();
    this.localLogout(url);
  }
}

export default new AuthService();
